import { ChangeEvent, useCallback, useMemo } from 'react';

import { useWatch } from '../watchUpdate';
import { MultiStateHook, useMultiState } from '../multiState';
import { AppSelectChangeEvent, ChangeEventLike } from 'components/Blocks';

/*
 **
 ** ---------> Memorization is VITAL for these <---------------------
 **
 */

type FormControlChangeEvent =
  | ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  | AppSelectChangeEvent<boolean>
  | ChangeEventLike<number>
  | ChangeEventLike<number[]>;

export type FormControlHook<DefaultValues> = Omit<MultiStateHook<DefaultValues>, 'values'> & {
  handleChange: (event: FormControlChangeEvent) => void;

  // temp as we refactor around
  state: MultiStateHook<DefaultValues>['values'];
};

export type FormControlDispatchAction<DefaultValues> = MultiStateHook<DefaultValues>['dispatch'];

interface ControlOptions {
  watch?: boolean;
}

export function useFormControl<FormValues extends object>(
  defaultValues: FormValues,
  { watch = false }: ControlOptions = {},
): FormControlHook<FormValues> {
  const { values, dispatch, resetDefault, peek, remove, set } = useMultiState(defaultValues);

  useWatch(defaultValues, () => watch && dispatch(defaultValues));

  const handleChange = useCallback(
    ({ target }: FormControlChangeEvent) => {
      const { id, name, value } = target;

      dispatch({
        [(id || name) ?? '__BAD_UPDATE__']: value,
      } as Partial<FormValues>);
    },
    // we know dispatch never changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const control = useMemo(
    () => ({
      state: values,
      dispatch,
      handleChange,
      resetDefault,
      peek,
      remove,
      set,
    }),
    // we know our handlers from multiState do not change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values],
  );

  return control as FormControlHook<FormValues>;
}
